import { formatEUR, type TransactionResponse } from '@budget/shared';
import { useState } from 'react';
import { getDeviceId } from '../api/device';
import { useCategories, useTransactions } from '../api/hooks';
import { TransactionEditModal } from '../components/TransactionEditModal';

const DAY_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

function groupByDay(items: TransactionResponse[]): [string, TransactionResponse[]][] {
  const groups = new Map<string, TransactionResponse[]>();
  for (const item of items) {
    const bucket = groups.get(item.occurredOn) ?? [];
    bucket.push(item);
    groups.set(item.occurredOn, bucket);
  }
  return [...groups.entries()];
}

function dayLabel(iso: string): string {
  // `T12:00` évite qu'un fuseau négatif fasse reculer la date d'un jour.
  return DAY_FORMAT.format(new Date(`${iso}T12:00:00`));
}

export function TransactionsScreen() {
  const transactions = useTransactions();
  const categories = useCategories();
  const [editing, setEditing] = useState<TransactionResponse | null>(null);
  const deviceId = getDeviceId();

  const byId = new Map((categories.data ?? []).map((category) => [category.id, category]));
  const items = transactions.data?.items ?? [];

  if (transactions.isPending) {
    return <p className="px-6 py-10 text-ink-muted">Chargement…</p>;
  }

  if (items.length === 0) {
    return (
      <p className="px-6 py-10 text-ink-secondary">
        Aucune écriture pour l’instant. Le bouton « + » attend votre première dépense.
      </p>
    );
  }

  return (
    <div className="px-5 pt-4">
      <h1 className="text-xl font-semibold">Écritures</h1>

      <div className="mt-4 flex flex-col gap-5">
        {groupByDay(items).map(([day, entries]) => (
          <section key={day}>
            <h2 className="text-xs font-medium text-ink-muted uppercase">{dayLabel(day)}</h2>
            <ul className="mt-2 overflow-hidden rounded-2xl border border-line bg-surface">
              {entries.map((entry) => {
                const category = entry.categoryId === null ? undefined : byId.get(entry.categoryId);
                const title =
                  entry.label !== '' ? entry.label : (category?.name ?? 'Sans libellé');

                // Une écriture ne se corrige que depuis le téléphone qui l'a
                // saisie. Celles sans appareil — échéances engendrées — n'ont
                // pas d'auteur, donc pas de propriétaire.
                const mine = entry.deviceId === null || entry.deviceId === deviceId;

                const body = (
                  <>
                    <span className="text-xl" aria-hidden="true">
                      {category?.icon ?? '•'}
                    </span>
                    <div className="min-w-0 flex-1 text-left">
                      <p className="truncate text-sm">{title}</p>
                      {/* Une écriture engendrée n'a pas été saisie par quelqu'un :
                          afficher un nom d'appareil serait trompeur. */}
                      <p className="truncate text-xs text-ink-muted">
                        {entry.recurringItemId !== null
                          ? 'Échéance fixe'
                          : entry.type === 'adjustment'
                            ? 'Recalage'
                            : entry.splitGroupId !== null
                              ? // Une part d'achat ventilé : le dire évite de
                                // croire à deux achats distincts.
                                `Ventilé · ${entry.paidBy ?? ''}`.trim().replace(/·$/, '')
                              : (entry.paidBy ?? '')}
                      </p>
                    </div>
                    <span
                      className={`tabular text-sm font-medium ${
                        entry.amountCents >= 0 ? 'text-success-text' : ''
                      }`}
                    >
                      {formatEUR(entry.amountCents)}
                    </span>
                  </>
                );

                return (
                  <li key={entry.id} className="border-b border-line last:border-b-0">
                    {mine ? (
                      <button
                        type="button"
                        onClick={() => setEditing(entry)}
                        aria-label={`Modifier ${title}`}
                        className="flex w-full items-center gap-3 px-3 py-3 active:bg-line"
                      >
                        {body}
                      </button>
                    ) : (
                      <div className="flex w-full items-center gap-3 px-3 py-3 opacity-70">
                        {body}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      {editing !== null && (
        <TransactionEditModal
          entry={editing}
          categories={categories.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
